import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Trophy, Star, Award, Zap, ArrowLeft, CheckCircle,
  Flame, Brain, Calendar, FileCheck, GraduationCap, Heart,
  Sunrise, Moon, Flag, Footprints, Target, Sparkles
} from 'lucide-react';
import SocialShare from '@/components/SocialShare';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge icon mapping
const getBadgeIcon = (iconName) => {
  const icons = {
    'footprints': Footprints,
    'brain': Brain,
    'flame': Flame,
    'zap': Zap,
    'calendar-check': Calendar,
    'file-check': FileCheck,
    'trophy': Trophy,
    'heart-handshake': Heart,
    'sunrise': Sunrise,
    'moon': Moon,
    'flag': Flag,
    'graduation-cap': GraduationCap,
  };
  return icons[iconName] || Award;
};

const ShareableAchievement = () => {
  const { type, id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAchievement();
  }, [type, id]);

  const fetchAchievement = async () => {
    try {
      const res = await axios.get(`${API}/achievements/share/${type}/${id}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Achievement not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <Target className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Achievement Not Found</h1>
          <p className="text-[#94A3B8] mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6A00] text-white rounded-xl hover:bg-[#FF6A00]/80 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `/achievements/share/${type}/${id}`;

  // Render Badge Achievement
  if (type === 'badge' && data) {
    const IconComponent = getBadgeIcon(data.badge?.icon);
    return (
      <div className="min-h-screen bg-[#060F22] py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Achievement Card */}
          <div className="bg-gradient-to-br from-[#0A1F44] to-[#0A1F44]/50 border border-[#FFD700]/30 rounded-3xl p-8 text-center relative overflow-hidden">
            {/* Background sparkles */}
            <div className="absolute inset-0 overflow-hidden">
              <Sparkles className="absolute top-4 left-4 w-6 h-6 text-[#FFD700]/20" />
              <Sparkles className="absolute top-12 right-8 w-4 h-4 text-[#FFD700]/30" />
              <Sparkles className="absolute bottom-8 left-12 w-5 h-5 text-[#FFD700]/20" />
              <Sparkles className="absolute bottom-16 right-4 w-6 h-6 text-[#FFD700]/25" />
            </div>

            <div className="relative">
              {/* NextGen Logo */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <img src="/logo.png" alt="NextGen Tech Pathways" className="h-8" />
                <span className="text-[#FF6A00] font-bold">NextGen Tech Pathways</span>
              </div>

              {/* Badge Icon */}
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#FFD700]/30 to-[#FF6A00]/20 flex items-center justify-center mx-auto mb-6 border-4 border-[#FFD700]/50">
                <IconComponent className="w-16 h-16 text-[#FFD700]" />
              </div>

              {/* Badge Info */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFD700]/20 rounded-full mb-4">
                <CheckCircle className="w-5 h-5 text-[#FFD700]" />
                <span className="text-[#FFD700] font-medium">Badge Earned!</span>
              </div>

              <h1 className="text-3xl font-bold text-white mb-2">{data.badge?.name}</h1>
              <p className="text-[#94A3B8] mb-6">{data.badge?.description}</p>

              {/* Student Info */}
              <div className="bg-[#060F22]/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-[#94A3B8] mb-1">Earned by</p>
                <p className="text-xl font-bold text-white">{data.student_name}</p>
                <p className="text-sm text-[#00FFE0] mt-2">
                  {new Date(data.earned_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>

              {/* Share Buttons */}
              <SocialShare
                type="badge"
                title={data.badge?.name}
                description={data.badge?.description}
                shareUrl={shareUrl}
              />
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-8">
            <p className="text-[#94A3B8] mb-4">Want to earn badges too?</p>
            <Link
              to="/enrollment"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6A00] text-white rounded-xl hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all font-medium"
            >
              <GraduationCap className="w-5 h-5" />
              Start Learning Today
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Render Level Achievement
  if (type === 'level' && data) {
    return (
      <div className="min-h-screen bg-[#060F22] py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Achievement Card */}
          <div 
            className="rounded-3xl p-8 text-center relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${data.level?.color}20, ${data.level?.color}10)`,
              border: `2px solid ${data.level?.color}50`
            }}
          >
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full" 
                style={{ background: `${data.level?.color}10` }}></div>
              <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full" 
                style={{ background: `${data.level?.color}10` }}></div>
            </div>

            <div className="relative">
              {/* NextGen Logo */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <img src="/logo.png" alt="NextGen Tech Pathways" className="h-8" />
                <span className="text-[#FF6A00] font-bold">NextGen Tech Pathways</span>
              </div>

              {/* Level Badge */}
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6 text-5xl font-black"
                style={{ 
                  background: `linear-gradient(135deg, ${data.level?.color}40, ${data.level?.color}20)`,
                  border: `4px solid ${data.level?.color}`
                }}
              >
                {data.level?.level}
              </div>

              {/* Level Up Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
                style={{ background: `${data.level?.color}30` }}>
                <Zap className="w-5 h-5" style={{ color: data.level?.color }} />
                <span className="font-medium" style={{ color: data.level?.color }}>Level Up!</span>
              </div>

              <h1 className="text-3xl font-bold text-white mb-2">{data.level?.name}</h1>
              <p className="text-[#94A3B8] mb-2">Level {data.level?.level} Achieved</p>
              <p className="text-2xl font-bold mb-6" style={{ color: data.level?.color }}>
                {data.total_points?.toLocaleString()} Points
              </p>

              {/* Student Info */}
              <div className="bg-[#060F22]/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-[#94A3B8] mb-1">Achieved by</p>
                <p className="text-xl font-bold text-white">{data.student_name}</p>
                <div className="flex items-center justify-center gap-4 mt-2 text-sm text-[#94A3B8]">
                  <span className="flex items-center gap-1">
                    <Award className="w-4 h-4 text-[#FFD700]" />
                    {data.badges_count} badges
                  </span>
                  <span className="flex items-center gap-1">
                    <Trophy className="w-4 h-4 text-[#FF6A00]" />
                    Rank #{data.rank}
                  </span>
                </div>
              </div>

              {/* Share Buttons */}
              <SocialShare
                type="level"
                title={data.level?.name}
                description={`${data.total_points?.toLocaleString()} points earned!`}
                shareUrl={shareUrl}
              />
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-8">
            <p className="text-[#94A3B8] mb-4">Ready to level up your tech skills?</p>
            <Link
              to="/enrollment"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF6A00] text-white rounded-xl hover:shadow-[0_0_20px_rgba(255,106,0,0.3)] transition-all font-medium"
            >
              <Star className="w-5 h-5" />
              Join NextGen Today
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ShareableAchievement;
